import { call, delay, put, select, takeLeading } from "redux-saga/effects";

import {
  getNextMusicTrack,
  getPlaylistData,
  isTrackInPlaylist,
  shuffleList,
} from "libs/game";
import { getPilotData, getPlanetData } from "libs/stats";
import {
  addEventLog,
  addLogMessage,
  animateAction,
  changePlanetPlaylist,
  disableMovement,
  disableMusic,
  enableMovement,
  enableMusic,
  endAnimation,
  enterShip,
  exitShip,
  getLogMessages,
  getMutedUsers,
  getSectorOnlinePlayers,
  hideInsideShip,
  inputControls,
  pauseMusic,
  playMusic,
  playNextMusicTrack,
  reactToLogMessage,
  setCurrentPlaylist,
  setCurrentTrack,
  setLogMessages,
  setMusicDisabled,
  setMusicEnabled,
  setMutedUsers,
  setPlayerDetails,
  setSectorOnlinePlayers,
  setTrackChange,
  showInsideShip,
  startMusicPlayer,
  stopMusicPlayer,
  switchDataScreen,
  viewFullComms,
  viewPlayerDetails,
} from "redux/actions";
import {
  getBuildInfo,
  getBuildingInfo,
  getLocationInfo,
  getMedallions,
  getShipInteriorInfo,
  getUserInfo,
  getUserSettings,
} from "redux/selectors/characterSelectors";
import { getFightStatus } from "redux/selectors/fightSelectors";
import { getMusicInfo, getPlayerDetails } from "redux/selectors/gameSelectors";
import {
  ControlsPayload,
  EventLogPayload,
  ExpandedMedallionsData,
  FightStatuses,
  LogMessage,
  LogMessagePayload,
  OnlinePlayerAvatarModel,
  PlayerData,
  PlayerDetailsModel,
} from "types";
import { FIGHT_DIALOG_STATUSES } from "utils/constants";
import {
  addReactionToLogMessage,
  createEventLog,
  createLogMessage,
  loadPlayerData,
  loadSectorOnlinePlayers,
  removeReactionFromLogMessage,
  sendDiscordMessage,
} from "utils/server/firebase";
import { menuControlsSaga, movementControlsSaga } from "./character";
import { fightControlsSaga } from "./fight";

export const UI_ANIMATIONS = {
  weaponsInstall: 1000,
  shieldsInstall: 1000,
  thrustersInstall: 1000,
  targetingSystemInstall: 1000,
  reactorCoreInstall: 1000,
};

export function* inputControlsSaga({ payload }: { payload: ControlsPayload }) {
  const fightStatus: FightStatuses = yield select(getFightStatus);
  const { isInsideShip } = yield select(getShipInteriorInfo);
  const { building } = yield select(getBuildingInfo);

  const isFightControls = fightStatus === "fighting";
  const isMenuControls = isInsideShip || building;
  const isMovementControls = !isFightControls && !isMenuControls;

  if (isFightControls) {
    yield call(fightControlsSaga, { payload });
  } else if (isMenuControls) {
    yield call(menuControlsSaga, { payload });
  } else if (isMovementControls) {
    yield call(movementControlsSaga, { payload });
  }
}

export function* getSectorOnlinePlayersSaga({
  payload: sector,
}: {
  payload: string;
}) {
  const onlinePlayers: OnlinePlayerAvatarModel[] =
    yield loadSectorOnlinePlayers(sector);

  yield put(setSectorOnlinePlayers(onlinePlayers));
}

function* enterShipSaga() {
  // Prevent entering ship when in a fight
  const status: FightStatuses = yield select(getFightStatus);
  const isFighting =
    status !== "notFighting" && !FIGHT_DIALOG_STATUSES.includes(status);
  if (isFighting) {
    return;
  }

  yield put(showInsideShip());

  yield put(disableMovement());
}

function* exitShipSaga() {
  yield put(hideInsideShip());

  yield put(enableMovement());
}

function* viewPlayerDetailsSaga({ payload: userId }: { payload: string }) {
  // Don't load player details if it's the same as the currently viewed player
  const currentPlayerDetails: PlayerDetailsModel | null = yield select(
    getPlayerDetails
  );
  if (currentPlayerDetails?.userId !== userId) {
    const playerData: PlayerData = yield loadPlayerData(userId);

    if (playerData) {
      yield put(setPlayerDetails(playerData));
    }
  }

  yield call(enterShipSaga);

  yield put(switchDataScreen("comms"));
}

function* viewFullCommsSaga() {
  yield call(enterShipSaga);

  yield put(switchDataScreen("comms"));
}

function* addLogMessageSaga({
  payload: { type, text = null, mobName = null, mobMedallions = null },
}: {
  payload: LogMessagePayload;
}) {
  // Get user ID and user name
  const { userId, userName } = yield select(getUserInfo);
  const medallions: ExpandedMedallionsData = yield select(getMedallions);
  const { pilot } = yield select(getBuildInfo);

  // Add new message in Firebase
  yield createLogMessage(
    type,
    userId,
    userName,
    medallions.total,
    text,
    mobName,
    mobMedallions
  );

  // Send chat messages to Discord
  if (type === "message" && text) {
    const pilotData = getPilotData(pilot);
    yield sendDiscordMessage(userName, text, pilotData.images.discord);
  }
}

function* reactToLogMessageSaga({ payload: message }: { payload: LogMessage }) {
  const { userName } = yield select(getUserInfo);

  const existingReaction = message.reactions.find(
    (reaction) => reaction.senderName === userName
  );

  // Toggle reaction
  if (existingReaction) {
    yield removeReactionFromLogMessage(message.id, existingReaction.id);
  } else {
    yield addReactionToLogMessage(message.id, userName);
    yield put(
      addEventLog({
        event: "react_to_log",
      })
    );
  }
}

function* getLogMessagesSaga({ payload: messages }: { payload: LogMessage[] }) {
  // Should already be limited by amount
  const logMessages = Object.entries(messages).map(([key, value]) => {
    return {
      ...value,
      id: key,
      reactions: Object.entries(value.reactions || []).map(([key, value]) => {
        return {
          ...value,
          id: key,
        };
      }),
    };
  });

  yield put(setLogMessages(logMessages));
}

function* getMutedUsersSaga({ payload: users }: { payload: string[] }) {
  const mutedUsers = Object.entries(users).map(([_key, value]) => {
    return value;
  });

  yield put(setMutedUsers(mutedUsers));
}

function* addEventLogSaga({ payload }: { payload: EventLogPayload }) {
  const { userId, userName } = yield select(getUserInfo);
  const { ship, pilot } = yield select(getBuildInfo);
  const medallions: ExpandedMedallionsData = yield select(getMedallions);

  const { event, eventParams } = payload;

  yield createEventLog(
    userId,
    userName,
    ship,
    pilot,
    medallions.total,
    event,
    eventParams
  );
}

function* playMusicSaga() {
  // Always check player setting before turning music on
  const { isMusicEnabled } = yield select(getUserSettings);

  if (!!isMusicEnabled) {
    yield put(startMusicPlayer());
  }
}

function* pauseMusicSaga() {
  yield put(stopMusicPlayer());
}

function* enableMusicSaga() {
  // Check if current track is in current playlist, if not switch to first song
  let { currentTrack, currentPlaylist } = yield select(getMusicInfo);
  const isInPlaylist = isTrackInPlaylist(currentTrack, currentPlaylist);

  yield put(setMusicEnabled());

  if (!isInPlaylist) {
    yield call(playNextMusicTrackSaga);
  }

  yield call(playMusicSaga);

  yield put(
    addEventLog({
      event: "enable_music",
    })
  );
}

function* disableMusicSaga() {
  yield put(setMusicDisabled());
  yield call(pauseMusicSaga);
}

function* playNextMusicTrackSaga() {
  let { currentTrack, currentPlaylist } = yield select(getMusicInfo);

  const nextTrack = getNextMusicTrack(currentTrack, currentPlaylist);

  yield put(setCurrentTrack(nextTrack));
  // Trigger play of new track, even if repeating same track
  yield put(setTrackChange());
}

export function* setTitleScreenPlaylistSaga() {
  const playlistData = getPlaylistData("titleScreen");

  const randomizedPlaylist = shuffleList(playlistData);

  yield put(setCurrentPlaylist(randomizedPlaylist));
}

function* changePlanetPlaylistSaga() {
  const { planet } = yield select(getLocationInfo);

  const planetData = getPlanetData(planet);
  const playlistData = getPlaylistData(planetData.playlist);

  const randomizedPlaylist = shuffleList(playlistData);

  yield put(setCurrentPlaylist(randomizedPlaylist));

  // Start playing new playlist when changing planets
  yield call(playNextMusicTrackSaga);
}

export function* uiAnimation(animation: keyof typeof UI_ANIMATIONS) {
  yield put(animateAction(animation));

  const animationTime = UI_ANIMATIONS[animation];
  yield delay(animationTime);

  // Reset animation
  yield put(endAnimation(animation));
}

export default function* gameSagas() {
  yield takeLeading(inputControls, inputControlsSaga);
  yield takeLeading(getSectorOnlinePlayers, getSectorOnlinePlayersSaga);
  yield takeLeading(enterShip, enterShipSaga);
  yield takeLeading(exitShip, exitShipSaga);
  yield takeLeading(viewPlayerDetails, viewPlayerDetailsSaga);
  yield takeLeading(viewFullComms, viewFullCommsSaga);
  yield takeLeading(addLogMessage, addLogMessageSaga);
  yield takeLeading(reactToLogMessage, reactToLogMessageSaga);
  yield takeLeading(getLogMessages, getLogMessagesSaga);
  yield takeLeading(getMutedUsers, getMutedUsersSaga);
  yield takeLeading(addEventLog, addEventLogSaga);
  yield takeLeading(playMusic, playMusicSaga);
  yield takeLeading(pauseMusic, pauseMusicSaga);
  yield takeLeading(enableMusic, enableMusicSaga);
  yield takeLeading(disableMusic, disableMusicSaga);
  yield takeLeading(playNextMusicTrack, playNextMusicTrackSaga);
  yield takeLeading(changePlanetPlaylist, changePlanetPlaylistSaga);
}
